/**
 * Whether a stamped analyst chart plot is drawable (plots plan T), shared by
 * the analyst tab and the featured routing so neither imports the other.
 * Shape only: the fields each kind the analyst tab draws needs present,
 * never what a value means.
 */

import { AnalystChartPlot } from '@digital-twins/shared-types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** The fields each kind the analyst tab draws needs present. */
const REQUIRED_ARRAYS: Record<string, Array<string>> = {
  'choice-bars': ['series'],
  'ranking-bars': ['items'],
  'grouped-bars': ['rows', 'categories'],
  'stacked-100-bars': ['rows', 'categories'],
  'heat-grid': ['rows', 'columns'],
  'diverging-likert': ['rows'],
}

/** A stamped plot the tab can draw, by the fields present. */
export function isDrawableAnalystPlot(plot: unknown): plot is AnalystChartPlot {
  if (!isRecord(plot) || typeof plot.kind !== 'string' || !isRecord(plot.data)) {
    return false
  }
  const required = REQUIRED_ARRAYS[plot.kind]
  if (!required) {
    return false
  }
  const data = plot.data
  return required.every((key) => Array.isArray(data[key]))
}

/**
 * The dense-cut rule (owner decision: stays 4 x 4), Python's
 * (`chart_plot.DENSE_CUT_MIN_*`, `table_plot.is_dense_table`): at least this
 * many groups (series or table rows) and cuts (categories or table columns)
 * draw as a heat grid.
 */
export const DENSE_CUT_MIN_GROUPS = 4
export const DENSE_CUT_MIN_CUTS = 4
